//@flow
import * as React from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import TimeUtils from 'common/utils/TimeUtils';

import EventGroupActions from 'common/redux/actions/eventGroup';
import {
    selectedDateSelector,
    shiftIdSelector,
    isSelectedDatePresentAndToday,
    activeTeamIdsSelector
} from 'common/redux/selectors/queryParamsSelectors';
import { eventGroupMapSelector } from 'common/redux/selectors/eventGroup';
import { clientIdSelector } from 'common/redux/selectors/sessionSelector';
import { getNotificationState } from 'common/redux/selectors/appPreferences';

import { FetchTypeEnum } from 'common/enums/FetchEnum';

import type { Session } from 'common/models/internal/Session';
import ControlledSetInterval from 'common/utils/ControlledSetInterval';
import DateTimeUtils, { CustomDateFormats } from 'common/utils/DateTimeUtils';

const poller = new ControlledSetInterval();
type InternalProps = {
    clientId: ?string,
    selectedDate: string,
    shiftId: string,
    teamIds: string,
    session: ?Session,
    fetchEventGroup: (
        clientId: ?string,
        date: string,
        shiftId: string,
        teamIds: string,
        userId: ?string,
        location: Object,
        fetchType: FetchTypeEnum
    ) => void,
    location: Object,
    today: string,
    isTodaySelected: boolean,
    notificationState: string
};

const DataPollingContainerHOC = (options: Object) => {
    return Component => {
        class DataPollingContainer extends React.PureComponent<
            InternalProps,
            void
        > {
            eventPoller: ControlledSetInterval;

            constructor(props) {
                super(props);
                this.eventPoller = poller.configurePoller({
                    callback: () => this.fetchData(),
                    timeout: options.timeout || 25000,
                    autoTerminateOn4xx: false
                });
            }

            UNSAFE_componentWillMount() {
                this.fetchData(FetchTypeEnum.Initial);

                this.eventPoller.startPolling();
            }

            UNSAFE_componentWillReceiveProps(nextProps: InternalProps) {
                const isDifferent = this.compareWith(this.props, nextProps, [
                    'clientId',
                    'shiftId',
                    'selectedDate',
                    'teamIds'
                ]);
                if (isDifferent) {
                    this.fetchData(FetchTypeEnum.Periodic, nextProps);
                }
            }

            componentWillUnmount() {
                this.eventPoller.terminatePolling();
            }

            compareWith = (
                props: InternalProps,
                nextProps: InternalProps,
                properties: Array<string> = []
            ): boolean => {
                let hasChanged = false;
                _.forEach(properties, property => {
                    if (!_.isEqual(props[property], nextProps[property])) {
                        hasChanged = true;
                    }
                });
                return hasChanged;
            };

            fetchData = (fetchType, nextProps): void => {
                const props = nextProps || this.props;
                const type = fetchType || FetchTypeEnum.Periodic;

                const {
                    pollFetchEventGroup,
                    fetchEventGroup,
                    clientId,
                    shiftId,
                    teamIds,
                    location,
                    today,
                    isTodaySelected,
                    notificationState
                } = props;

                let date = today;
                if (!clientId) return;
                if (options && options.date) {
                    ({ date } = options);
                }

                if (!isTodaySelected && notificationState === 'on') {
                    /**
                     * Once all the params for fetch metrics are available
                     * poller.setPollerState with apiModel, params to attach Interceptors
                     */
                    pollFetchEventGroup(
                        clientId,
                        date,
                        shiftId,
                        teamIds,
                        null,
                        this.eventPoller
                    );
                    return fetchEventGroup(
                        clientId,
                        date,
                        shiftId,
                        teamIds,
                        null,
                        location,
                        type
                    );
                }
            };

            render() {
                return <Component {...this.props} />;
            }
        }

        const mapStateToProps = (
            state: Object,
            ownProps: InternalProps
        ): Object => {
            const clientId = clientIdSelector(state, ownProps);
            const shiftId = shiftIdSelector(state, ownProps);
            const selectedDate = selectedDateSelector(state, ownProps);
            const teamIds = activeTeamIdsSelector(state, ownProps);
            const eventGroups = eventGroupMapSelector(state, ownProps, options);
            const session = state.session.get('session');
            const today = DateTimeUtils.customFormat(
                TimeUtils.now(),
                CustomDateFormats.API_DATE
            );
            const isTodaySelected = isSelectedDatePresentAndToday(
                state,
                ownProps
            );
            const notificationState = getNotificationState(state);

            return {
                clientId,
                shiftId,
                selectedDate,
                teamIds,
                eventGroups,
                session,
                today,
                isTodaySelected,
                notificationState
            };
        };

        return connect(mapStateToProps, {
            fetchEventGroup: EventGroupActions.fetchEventGroup,
            pollFetchEventGroup: EventGroupActions.pollFetchEventGroup
        })(DataPollingContainer);
    };
};

export default DataPollingContainerHOC;
